import { Card } from "@exhousing/design-system";
import {
  Bell,
  ChevronRight,
  FileText,
  type LucideIcon,
  Stamp,
  UserCog,
} from "lucide-react";
import { Link } from "react-router-dom";

import { useMe } from "@/hooks/api/useAuth";

type Me = ReturnType<typeof useMe>["data"];

const isSuperAdmin = (me: Me) => Boolean(me?.roles?.includes("super_admin"));
const canManageUsers = (me: Me) =>
  Boolean(me?.permissions?.includes("user.view") || isSuperAdmin(me));
const canViewDocuments = (me: Me) =>
  Boolean(me?.permissions?.includes("document.view") || isSuperAdmin(me));

type SettingLink = {
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
  show?: (me: Me) => boolean;
};

const LINKS: SettingLink[] = [
  {
    to: "/document-templates",
    label: "Document templates",
    description: "Edit the contract, letter and invoice templates the studio generates.",
    icon: FileText,
    show: canViewDocuments,
  },
  {
    to: "/notifications",
    label: "Notifications",
    description: "The catalog of automated emails sent to candidates, tenants and owners.",
    icon: Bell,
    show: canViewDocuments,
  },
  {
    to: "/letterhead",
    label: "Letterhead",
    description: "Your logo, address and footer applied to generated documents.",
    icon: Stamp,
    show: canManageUsers,
  },
  {
    to: "/team",
    label: "Team",
    description: "Invite colleagues and manage their roles and access.",
    icon: UserCog,
    show: canManageUsers,
  },
];

export function SettingsPage() {
  const { data: me } = useMe();
  const links = LINKS.filter((l) => !l.show || l.show(me));

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header>
        <h1 className="font-display text-3xl font-medium tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Templates, branding and your team — everything that shapes the rest of the
          workspace.
        </p>
      </header>

      <div className="space-y-3">
        {links.map((link) => (
          <Link key={link.to} to={link.to} className="block">
            <Card className="flex items-center gap-4 p-4 transition-colors hover:border-primary">
              <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
                <link.icon className="size-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{link.label}</p>
                <p className="text-sm text-muted-foreground">{link.description}</p>
              </div>
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
